import { cn } from '@/lib/utils';

type Level = 'low' | 'medium' | 'high';

interface Props {
  label: string;
  value: Level | null | undefined;
  onChange: (value: Level | null) => void;
  disabled?: boolean;
}

const LEVELS: Level[] = ['low', 'medium', 'high'];
const LEVEL_LABEL: Record<Level, string> = { low: 'Low', medium: 'Medium', high: 'High' };

// Each level lights up in its own clinical hue when active. Reading the
// bar at a glance, "high" should pull the eye more than "low" — that's the
// whole point of a severity segmentation.
const ACTIVE_TONE: Record<Level, string> = {
  low: 'bg-primary-50 text-primary-700 ring-primary-200/70',
  medium: 'bg-warning-50 text-warning-700 ring-warning-300/70',
  high: 'bg-[#fdecec] text-error-700 ring-[#f1c1c1]/60',
};

export function AnxietyRiskToggle({ label, value, onChange, disabled = false }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-accent-ink text-[13px] font-medium" aria-hidden>
        {label}
      </span>
      <fieldset className="border-accent-ink/10 inline-flex rounded-full border bg-neutral-50/80 p-0.5">
        <legend className="sr-only">{label}</legend>
        {LEVELS.map((level) => {
          const isActive = value === level;
          return (
            // Toggle-button-group pattern (APG): each option is a pressed
            // <button>, the wrapper announces the group. We avoid `role="radio"`
            // here because radios can't be deselected, but clinicians need to
            // clear a field.
            <button
              key={level}
              type="button"
              aria-pressed={isActive}
              disabled={disabled}
              onClick={() => {
                onChange(value === level ? null : level);
              }}
              className={cn(
                'flex-1 rounded-full px-3 py-1 text-[12px] font-medium transition-all duration-150',
                'focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:outline-none',
                isActive
                  ? `${ACTIVE_TONE[level]} ring-1`
                  : 'hover:text-accent-ink text-neutral-500',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {LEVEL_LABEL[level]}
            </button>
          );
        })}
      </fieldset>
    </div>
  );
}
